import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { HeaderTitleService } from '../../../services/header-title.service';
import {
  SuperadminService,
  Organization,
  GlobalUser,
} from '../../../services/superadmin.service';
import { MasterDataService } from '../../../services/master-data.service';
import { InviteUserComponent } from '../invite-user/invite-user.component';
import { globalUser } from '../../../helpers/globalFields';
import { MasterPermission } from '../../../interfaces/master-permission';
import { UserpermissionService } from '../../../services/userpermission.service';
import { PermissionRow } from '../panel/panel.component';
import { AddExistingUserDialogComponent } from '../add-existing-user-dialog/add-existing-user-dialog.component';
import { CreateOrganizationDialogComponent } from '../create-organization-dialog/create-organization-dialog.component';
import { Router } from '@angular/router';
import { AddGlobalPermissionDialogComponent } from '../add-global-permission-dialog/add-global-permission-dialog.component';

/**
 * Superadmin dashboard for system-wide administration.
 *
 * Provides three panels:
 *   1. Organizations  — create and delete organizations
 *   2. Global Users   — view users with org-independent permissions,
 *                       revoke global permissions, assign to an org
 *   3. Pending Invites — view unaccepted invites and resend them
 *
 * Requires `system.admin` permission. The AuthGuard enforces this at the
 * route level; the backend enforces it on every endpoint.
 *
 * @author julian.f.verhey
 */
@Component({
  selector: 'app-superadmin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatChipsModule,
    MatTabsModule,
    MatSelectModule,
    MatCheckboxModule,
  ],
  templateUrl: './superadmin.component.html',
  styleUrl: './superadmin.component.scss',
})
export class SuperadminComponent implements OnInit, OnDestroy {
  // ── Organizations ──────────────────────────────────────────────────────────
  organizations: Organization[] = [];
  newOrgName = '';
  loadingOrgs = false;
  savingOrg = false;

  // ── Global users ───────────────────────────────────────────────────────────
  globalUsers: GlobalUser[] = [];
  loadingGlobal = false;

  /** For assign-to-org flow */
  assigningUser: GlobalUser | null = null;
  selectedOrgId: number | null = null;
  selectedPermissionIds: Set<number> = new Set();
  masterPermissions: MasterPermission[] = [];
  assignSaving = false;

  // ── Pending invites ────────────────────────────────────────────────────────
  pendingUsers: any[] = [];
  loadingPending = false;
  resendingId: number | null = null;

  private userSub?: Subscription;

  constructor(
    private superadminService: SuperadminService,
    private masterDataService: MasterDataService,
    private headerTitleService: HeaderTitleService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private permissionService: UserpermissionService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.headerTitleService.setTitle('Superadmin');
    this.loadAll();

    this.userSub = globalUser.getUserStream().subscribe((user) => {
      if (user) this.loadAll();
    });

    this.userSub = globalUser.getUserStream().subscribe((user) => {
      if (user) {
        const perms: string[] = user.permissions ?? [];

        if (!perms.includes('system.admin')) {
          // Lost superadmin — redirect to org admin or ticket list
          if (perms.includes('organization.admin')) {
            this.router.navigate(['/admin']);
          } else {
            this.router.navigate(['/ticket/list']);
          }
          return;
        }

        this.loadAll();
      }
    });
  }

  onInviteUserForOrg(org: Organization): void {
    const ref = this.dialog.open(InviteUserComponent, {
      data: {
        orgName: org.org_name,
        isSuperAdmin: false, // org is already fixed — no org picker needed
        organizations: [],
        targetOrgId: org.id,
        hideGlobalPermissions: true,
      },
      width: '500px',
      panelClass: 'custom-dialog-container',
    });
    ref.afterClosed().subscribe((sent) => {
      if (sent) this.loadOrgUsers(org.id); // reload this org's user grid on success
    });
  }

  /**
   * Load all three data sets in parallel on init and org/user change.
   */
  loadAll(): void {
    this.loadOrganizations();
    this.loadGlobalUsers();
    this.loadPendingUsers();
    this.loadDanglingUsers();
    this.loadAllUsers();
    this.masterDataService.getMasterPermissionList().subscribe({
      next: (perms) => {
        this.masterPermissions = perms;
        this.globalPermissions = perms.filter((p) =>
          this.GLOBAL_PERMISSION_KEYS.includes(p.mpm_type),
        );
      },
    });
  }

  // ── Organizations ──────────────────────────────────────────────────────────

  loadOrganizations(): void {
    this.loadingOrgs = true;
    this.superadminService.getOrganizations().subscribe({
      next: (res) => {
        this.organizations = res.data ?? [];
        this.applyOrganizationSearch();
        this.applyallGlobalPermissionUserSearchSearch();
        this.loadingOrgs = false;
      },
      error: () => (this.loadingOrgs = false),
    });
  }

  createOrganization(): void {
    const name = this.newOrgName.trim();
    if (!name) return;
    this.savingOrg = true;
    this.superadminService.createOrganization(name).subscribe({
      next: () => {
        this.snackBar.open(`Organization "${name}" created.`, 'Close', {
          duration: 3000,
        });
        this.newOrgName = '';
        this.savingOrg = false;
        this.loadOrganizations();
      },
      error: () => (this.savingOrg = false),
    });
  }

  deleteOrganization(org: Organization): void {
    const confirmed = window.confirm(
      `Delete organization "${org.org_name}"?\n\nAll org-scoped permissions will be revoked. Users will not be deleted but may become orphaned. This cannot be undone.`,
    );
    if (!confirmed) return;

    this.superadminService.deleteOrganization(org.id).subscribe({
      next: () => {
        this.snackBar.open(`"${org.org_name}" deleted.`, 'Close', {
          duration: 3000,
        });
        this.loadOrganizations();
        this.loadGlobalUsers(); // some users may have become orphaned
      },
    });
  }

  // ── Global users ───────────────────────────────────────────────────────────

  loadGlobalUsers(): void {
    this.loadingGlobal = true;
    this.superadminService.getGlobalUsers().subscribe({
      next: (res) => {
        this.globalUsers = res.data ?? [];
        this.rebuildGlobalDraft();
        this.loadingGlobal = false;
      },
      error: () => (this.loadingGlobal = false),
    });
  }

  savingGlobal = false;

  // Original state snapshot for dirty checking
  private globalOriginal: Record<number, Record<number, boolean>> = {};

  private rebuildGlobalDraft(): void {
    this.globalDraft = {};
    this.globalOriginal = {};
    for (const user of this.globalUsers) {
      this.globalDraft[user.usr_id] = {};
      this.globalOriginal[user.usr_id] = {};
      for (const perm of this.globalPermissions) {
        const has = user.permissions.some((p: any) => p.key === perm.mpm_type);
        this.globalDraft[user.usr_id][perm.id] = has;
        this.globalOriginal[user.usr_id][perm.id] = has;
      }
    }
  }

  userHasGlobalPerm(user: GlobalUser, permId: number): boolean {
    return this.globalDraft[user.usr_id]?.[permId] ?? false;
  }

  hasGlobalPermChange(user: GlobalUser, permId: number): boolean {
    return (
      (this.globalDraft[user.usr_id]?.[permId] ?? false) !==
      (this.globalOriginal[user.usr_id]?.[permId] ?? false)
    );
  }

  hasAnyGlobalPendingChanges(): boolean {
    return this.globalUsers.some((user) =>
      this.globalPermissions.some((perm) =>
        this.hasGlobalPermChange(user, perm.id),
      ),
    );
  }

  toggleGlobalPerm(user: GlobalUser, permId: number, checked: boolean): void {
    if (!this.globalDraft[user.usr_id]) this.globalDraft[user.usr_id] = {};
    this.globalDraft[user.usr_id][permId] = checked;
  }

  submitGlobalChanges(): void {
    const creates$: Observable<any>[] = [];
    const deletes$: Observable<any>[] = [];
    const affectedUserIds = new Set<number>();

    for (const user of this.globalUsers) {
      for (const perm of this.globalPermissions) {
        const wasChecked = this.globalOriginal[user.usr_id]?.[perm.id] ?? false;
        const isChecked = this.globalDraft[user.usr_id]?.[perm.id] ?? false;

        if (!wasChecked && isChecked) {
          creates$.push(
            this.permissionService.createPermission(user.usr_id, perm.id),
          );
          affectedUserIds.add(user.usr_id);
        } else if (wasChecked && !isChecked) {
          const entryId = user.permissions.find(
            (p: any) => p.key === perm.mpm_type,
          )?.entry_id;
          if (entryId) {
            deletes$.push(
              this.superadminService.revokeGlobalPermission(entryId),
            );
            affectedUserIds.add(user.usr_id);
          }
        }
      }
    }

    const all$ = [...creates$, ...deletes$];
    if (!all$.length) return;

    this.savingGlobal = true;
    forkJoin(all$).subscribe({
      next: () => {
        affectedUserIds.forEach((uid) =>
          this.permissionService.notifyUser(uid).subscribe(),
        );
        this.snackBar.open('Global permissions updated.', 'Close', {
          duration: 3000,
        });
        this.savingGlobal = false;
        this.loadGlobalUsers();
      },
      error: () => (this.savingGlobal = false),
    });
  }

  /**
   * Open the assign-to-org panel for a global user.
   */
  startAssign(user: GlobalUser): void {
    this.assigningUser = user;
    this.selectedOrgId = null;
    this.selectedPermissionIds = new Set();
  }

  cancelAssign(): void {
    this.assigningUser = null;
  }

  toggleAssignPermission(id: number): void {
    if (this.selectedPermissionIds.has(id)) {
      this.selectedPermissionIds.delete(id);
    } else {
      this.selectedPermissionIds.add(id);
    }
  }

  confirmAssign(): void {
    if (!this.assigningUser || !this.selectedOrgId) return;
    this.assignSaving = true;

    this.superadminService
      .assignUserToOrganization(
        this.assigningUser.usr_id,
        this.selectedOrgId,
        Array.from(this.selectedPermissionIds),
      )
      .subscribe({
        next: () => {
          this.snackBar.open(
            `${this.assigningUser!.usr_name} assigned to organization.`,
            'Close',
            { duration: 3000 },
          );
          this.assigningUser = null;
          this.assignSaving = false;
          this.loadGlobalUsers();
        },
        error: () => (this.assignSaving = false),
      });
  }

  // ── Pending invites ────────────────────────────────────────────────────────

  loadPendingUsers(): void {
    this.loadingPending = true;
    this.superadminService.getPendingUsers().subscribe({
      next: (res) => {
        this.pendingUsers = res.data ?? [];
        this.loadingPending = false;
      },
      error: () => (this.loadingPending = false),
    });
  }

  resendInvite(user: any): void {
    this.resendingId = user.id;
    this.superadminService.resendInvite(user.id).subscribe({
      next: (res) => {
        this.snackBar.open(`Invite resent to ${user.usr_MAIL}.`, 'Close', {
          duration: 3000,
        });
        this.resendingId = null;
        this.loadPendingUsers();
      },
      error: () => (this.resendingId = null),
    });
  }

  /**
   * Open the standard invite dialog with superadmin mode — shows org picker.
   */
  onInviteUser(): void {
    const ref = this.dialog.open(InviteUserComponent, {
      data: {
        orgName: 'System',
        isSuperAdmin: true,
        organizations: this.organizations,
      },
      width: '500px',
      panelClass: 'custom-dialog-container',
    });
    ref.afterClosed().subscribe((sent) => {
      if (sent) this.loadPendingUsers();
    });
  }

  onAssignOrgChange(): void {
    this.selectedPermissionIds = new Set();
  }

  getOrgName(orgId: number): string {
    return this.organizations.find((o) => o.id === orgId)?.org_name ?? '—';
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
  }

  // ── Org expansion ──────────────────────────────────────────────────────────
  expandedOrgId: number | null = null;
  orgRows: Record<number, PermissionRow[]> = {};
  orgPermissions: {
    id: number;
    key: string;
    label: string;
    category: string;
  }[] = [];
  loadingOrgId: number | null = null;
  savingOrgId: number | null = null;

  toggleOrg(org: Organization): void {
    if (this.expandedOrgId === org.id) {
      this.expandedOrgId = null;
      return;
    }
    this.expandedOrgId = org.id;
    this.loadOrgUsers(org.id);
  }

  loadOrgUsers(orgId: number): void {
    const GLOBAL_PERMISSIONS = ['report.global', 'system.admin'];
    this.loadingOrgId = orgId;
    this.superadminService.getUsersForOrg(orgId).subscribe({
      next: (res) => {
        if (!this.orgPermissions.length) {
          this.orgPermissions = this.masterPermissions
            .filter((p) => !GLOBAL_PERMISSIONS.includes(p.mpm_type))
            .map((p) => ({
              id: p.id,
              key: p.mpm_type,
              label: p.mpm_type.replace('.', ' '),
              category: p.mpm_type.split('.')[0],
            }));
          const order = ['ticket', 'report', 'user'];
          this.orgPermissions.sort((a, b) => {
            const ai = order.indexOf(a.category);
            const bi = order.indexOf(b.category);
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
          });
        }
        this.orgRows[orgId] = res.data.map((entry: any) =>
          this.buildOrgRow(entry),
        );
        this.loadingOrgId = null;
      },
      error: () => (this.loadingOrgId = null),
    });
  }

  private buildOrgRow(entry: any): PermissionRow {
    const original: PermissionRow['original'] = {};
    const draft: PermissionRow['draft'] = {};
    for (const perm of this.orgPermissions) {
      const ref = (entry.permissions as any[]).find((p: any) =>
        typeof p === 'string' ? p === perm.key : p.key === perm.key,
      );
      const entryId = ref && typeof ref === 'object' ? ref.entry_id : null;
      original[perm.key] = { checked: !!ref, entryId };
      draft[perm.key] = !!ref;
    }
    return {
      usr_id: entry.usr_id,
      usr_name: entry.usr_name,
      usr_mail: entry.usr_mail,
      original,
      draft,
    };
  }

  hasOrgPendingChanges(orgId: number): boolean {
    return (this.orgRows[orgId] ?? []).some((row) =>
      this.orgPermissions.some(
        (p) => row.draft[p.key] !== row.original[p.key].checked,
      ),
    );
  }

  submitOrgChanges(orgId: number): void {
    const rows = this.orgRows[orgId] ?? [];
    const creates$: Observable<any>[] = [];
    const deletes$: Observable<any>[] = [];
    const affectedUserIds = new Set<number>();

    for (const row of rows) {
      for (const perm of this.orgPermissions) {
        const wasChecked = row.original[perm.key].checked;
        const isChecked = row.draft[perm.key];
        const entryId = row.original[perm.key].entryId;
        if (!wasChecked && isChecked) {
          creates$.push(
            this.permissionService.createPermission(row.usr_id, perm.id, orgId),
          );
          affectedUserIds.add(row.usr_id);
        } else if (wasChecked && !isChecked && entryId !== null) {
          deletes$.push(this.permissionService.deletePermission(entryId));
          affectedUserIds.add(row.usr_id);
        }
      }
    }

    const all$ = [...creates$, ...deletes$];
    if (!all$.length) return;

    this.savingOrgId = orgId;
    forkJoin(all$).subscribe({
      next: () => {
        affectedUserIds.forEach((uid) =>
          this.permissionService.notifyUser(uid).subscribe(),
        );
        this.snackBar.open('Permissions updated.', 'Close', { duration: 3000 });
        this.savingOrgId = null;
        this.loadOrgUsers(orgId);
      },
      error: () => (this.savingOrgId = null),
    });
  }

  removeUserFromOrg(orgId: number, row: PermissionRow): void {
    const confirmed = window.confirm(
      `Remove ${row.usr_name} (${row.usr_mail}) from ${this.getOrgName(orgId)}?\n\nThis will revoke all their permissions for this organization. Their account will not be deleted.`,
    );
    if (!confirmed) return;

    this.permissionService.deleteUserFromOrganization(row.usr_id).subscribe({
      next: () => {
        this.snackBar.open(`${row.usr_name} has been removed.`, 'Close', {
          duration: 3000,
        });
        this.loadOrgUsers(orgId);
      },
      error: () => {
        this.snackBar.open('Failed to remove user.', 'Close', {
          duration: 4000,
        });
      },
    });
  }

  // ── Dangling users ─────────────────────────────────────────────────────────
  danglingUsers: any[] = [];
  loadingDangling = false;

  loadDanglingUsers(): void {
    this.loadingDangling = true;
    this.superadminService.getDanglingUsers().subscribe({
      next: (res) => {
        this.danglingUsers = res.data ?? [];
        this.loadingDangling = false;
      },
      error: () => (this.loadingDangling = false),
    });
  }

  // ── Add existing user to org ───────────────────────────────────────────────
  availableUsers: any[] = [];
  addingUserToOrg: Organization | null = null;
  selectedExistingUserId: number | null = null;
  addingUserSaving = false;

  startAddExistingUser(org: Organization): void {
    this.addingUserToOrg = org;
    this.selectedExistingUserId = null;
    this.selectedPermissionIds = new Set();

    // Load users that are NOT already in this org
    this.superadminService.getUsersNotInOrg(org.id).subscribe({
      next: (res) => (this.availableUsers = res.data ?? []),
    });
  }

  cancelAddExisting(): void {
    this.addingUserToOrg = null;
    this.availableUsers = [];
  }

  confirmAddExisting(orgId: number): void {
    if (!this.selectedExistingUserId || !this.selectedPermissionIds.size)
      return;
    this.addingUserSaving = true;

    this.superadminService
      .assignUserToOrganization(
        this.selectedExistingUserId,
        orgId,
        Array.from(this.selectedPermissionIds),
      )
      .subscribe({
        next: () => {
          this.snackBar.open('User added to organization.', 'Close', {
            duration: 3000,
          });
          this.addingUserToOrg = null;
          this.addingUserSaving = false;
          this.availableUsers = [];
          this.loadOrgUsers(orgId);
        },
        error: () => (this.addingUserSaving = false),
      });
  }
  clearSelectedPermissions(): void {
    this.selectedPermissionIds = new Set();
  }

  onAddExistingUser(org: Organization): void {
    // Ensure orgPermissions are loaded before opening dialog
    const openDialog = () => {
      const ref = this.dialog.open(AddExistingUserDialogComponent, {
        data: {
          org,
          orgPermissions: this.orgPermissions,
        },
        width: '500px',
        panelClass: 'custom-dialog-container',
      });
      ref.afterClosed().subscribe((added) => {
        if (added) this.loadOrgUsers(org.id);
      });
    };

    if (!this.orgPermissions.length) {
      // orgPermissions not loaded yet — fetch master permissions first
      this.masterDataService.getMasterPermissionList().subscribe({
        next: (perms) => {
          const GLOBAL_PERMISSIONS = ['report.global', 'system.admin'];
          const order = ['ticket', 'report', 'user'];
          this.orgPermissions = perms
            .filter((p) => !GLOBAL_PERMISSIONS.includes(p.mpm_type))
            .map((p) => ({
              id: p.id,
              key: p.mpm_type,
              label: p.mpm_type.replace('.', ' '),
              category: p.mpm_type.split('.')[0],
            }))
            .sort((a, b) => {
              const ai = order.indexOf(a.category);
              const bi = order.indexOf(b.category);
              return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
            });
          openDialog();
        },
      });
    } else {
      openDialog();
    }
  }

  // ── Global permission grid ─────────────────────────────────────────────────
  globalPermissions: MasterPermission[] = [];
  globalDraft: Record<number, Record<number, boolean>> = {}; // userId -> permId -> checked

  private readonly GLOBAL_PERMISSION_KEYS = ['report.global', 'system.admin'];

  onCreateOrganization(): void {
    const ref = this.dialog.open(CreateOrganizationDialogComponent, {
      width: '420px',
      panelClass: 'custom-dialog-container',
    });
    ref.afterClosed().subscribe((created) => {
      if (created) this.loadOrganizations();
    });
  }

  deleteUser(user: any): void {
    const confirmed = window.confirm(
      `Permanently delete ${user.usr_name} (${user.usr_mail})?\n\nThis will remove their account and all associated permissions. This cannot be undone.`,
    );
    if (!confirmed) return;

    this.superadminService.deleteUser(user.usr_id).subscribe({
      next: () => {
        this.snackBar.open(`${user.usr_name} has been deleted.`, 'Close', {
          duration: 3000,
        });
        this.loadDanglingUsers();
      },
      error: () => {
        this.snackBar.open('Failed to delete user.', 'Close', {
          duration: 4000,
        });
      },
    });
  }

  deletePendingUser(user: any): void {
    const confirmed = window.confirm(
      `Cancel invitation for ${user.usr_MAIL}?\n\nThe invite link will be invalidated and the account will be removed. This cannot be undone.`,
    );
    if (!confirmed) return;

    this.superadminService.deletePendingUser(user.id).subscribe({
      next: () => {
        this.snackBar.open(
          `Invitation for ${user.usr_MAIL} cancelled.`,
          'Close',
          { duration: 3000 },
        );
        this.loadPendingUsers();
      },
      error: () => {
        this.snackBar.open('Failed to cancel invitation.', 'Close', {
          duration: 4000,
        });
      },
    });
  }

  // ── All users ──────────────────────────────────────────────────────────────
  allUsers: any[] = [];
  loadingAllUsers = false;
  allUsersSearch = '';
  togglingUserId: number | null = null;

  get filteredAllUsers(): any[] {
    const q = this.allUsersSearch.toLowerCase();
    if (!q) return this.allUsers;
    return this.allUsers.filter(
      (u) =>
        u.usr_name.toLowerCase().includes(q) ||
        u.usr_mail.toLowerCase().includes(q),
    );
  }

  loadAllUsers(): void {
    this.loadingAllUsers = true;
    this.superadminService.getAllUsers().subscribe({
      next: (res) => {
        this.allUsers = res.data ?? [];
        this.loadingAllUsers = false;
      },
      error: () => (this.loadingAllUsers = false),
    });
  }

  toggleUserActive(user: any): void {
    this.togglingUserId = user.usr_id;
    this.superadminService
      .toggleUserActive(user.usr_id, !user.usr_active)
      .subscribe({
        next: () => {
          user.usr_active = !user.usr_active;
          this.togglingUserId = null;
          this.snackBar.open(
            `${user.usr_name} has been ${user.usr_active ? 'activated' : 'deactivated'}.`,
            'Close',
            { duration: 3000 },
          );
        },
        error: () => (this.togglingUserId = null),
      });
  }

  deleteAllUser(user: any): void {
    const confirmed = window.confirm(
      `Permanently delete ${user.usr_name} (${user.usr_mail})?\n\nThis will remove their account and all permissions across all organizations. This cannot be undone.`,
    );
    if (!confirmed) return;

    this.superadminService.deleteUser(user.usr_id).subscribe({
      next: () => {
        this.snackBar.open(`${user.usr_name} has been deleted.`, 'Close', {
          duration: 3000,
        });
        this.loadAllUsers();
        this.loadDanglingUsers(); // refresh in case they were dangling
      },
      error: () => {
        this.snackBar.open('Failed to delete user.', 'Close', {
          duration: 4000,
        });
      },
    });
  }

  // ── Add global permission to existing user ─────────────────────────────────
  globalUserSearch = '';
  globalUserSearchResults: any[] = [];
  searchingGlobalUsers = false;
  addGlobalPanelOpen = false;
  selectedGlobalUserId: number | null = null;
  selectedGlobalPermIds: Set<number> = new Set();

  toggleAddGlobalPanel(): void {
    this.addGlobalPanelOpen = !this.addGlobalPanelOpen;
    if (!this.addGlobalPanelOpen) this.resetAddGlobalPanel();
  }

  resetAddGlobalPanel(): void {
    this.globalUserSearch = '';
    this.globalUserSearchResults = [];
    this.selectedGlobalUserId = null;
    this.selectedGlobalPermIds = new Set();
  }

  searchGlobalUsers(): void {
    const q = this.globalUserSearch.trim();
    if (!q) {
      this.globalUserSearchResults = [];
      return;
    }

    this.searchingGlobalUsers = true;
    this.superadminService.getAllUsers().subscribe({
      next: (res) => {
        const all = res.data ?? [];
        const lower = q.toLowerCase();
        // Exclude users who already have ALL global permissions
        this.globalUserSearchResults = all.filter(
          (u) =>
            u.usr_name.toLowerCase().includes(lower) ||
            u.usr_mail.toLowerCase().includes(lower),
        );
        this.searchingGlobalUsers = false;
      },
      error: () => (this.searchingGlobalUsers = false),
    });
  }

  confirmAddGlobalPermissions(): void {
    if (!this.selectedGlobalUserId || !this.selectedGlobalPermIds.size) return;

    const creates$ = Array.from(this.selectedGlobalPermIds).map((permId) =>
      this.permissionService.createPermission(
        this.selectedGlobalUserId!,
        permId,
      ),
    );

    forkJoin(creates$).subscribe({
      next: () => {
        this.permissionService
          .notifyUser(this.selectedGlobalUserId!)
          .subscribe();
        this.snackBar.open('Global permissions granted.', 'Close', {
          duration: 3000,
        });
        this.resetAddGlobalPanel();
        this.addGlobalPanelOpen = false;
        this.loadGlobalUsers();
      },
      error: () => {
        this.snackBar.open('Failed to grant permissions.', 'Close', {
          duration: 4000,
        });
      },
    });
  }

  // Property declarations
  allOrganizationSearch: string = '';
  allOrganizations: any[] = []; // raw data from API
  filteredOrganizations: any[] = []; // bound to your table
  // Triggered by (ngModelChange) or called manually
  applyOrganizationSearch(): void {
    const term = this.allOrganizationSearch?.trim().toLowerCase() ?? '';

    if (!term) {
      this.filteredOrganizations = [...this.organizations];
      return;
    }

    this.filteredOrganizations = this.organizations.filter((org) =>
      org.org_name?.toLowerCase().includes(term),
    );
  }

  revokeAllGlobalPermissions(user: GlobalUser): void {
    const confirmed = window.confirm(
      `Strip all global permissions from ${user.usr_name} (${user.usr_mail})?\n\nThey will lose system.admin and report.global access immediately. This cannot be undone.`,
    );
    if (!confirmed) return;

    this.superadminService.revokeAllGlobalPermissions(user.usr_id).subscribe({
      next: () => {
        this.permissionService.notifyUser(user.usr_id).subscribe();
        this.snackBar.open(
          `All global permissions revoked from ${user.usr_name}.`,
          'Close',
          { duration: 3000 },
        );
        this.loadGlobalUsers();
      },
      error: () => {
        this.snackBar.open('Failed to revoke permissions.', 'Close', {
          duration: 4000,
        });
      },
    });
  }

  // Add these properties
  allGlobalPermissionUserSearch: string = '';
  filteredGlobalUsers: GlobalUser[] = [];
  // Add this method
  applyallGlobalPermissionUserSearchSearch(): void {
    const term = this.allGlobalPermissionUserSearch?.trim().toLowerCase() ?? '';

    if (!term) {
      this.filteredGlobalUsers = [...this.globalUsers];
      return;
    }

    this.filteredGlobalUsers = this.globalUsers.filter(
      (u) =>
        u.usr_name?.toLowerCase().includes(term) ||
        u.usr_mail?.toLowerCase().includes(term),
    );
  }

  onAddGlobalPermission(): void {
    const ref = this.dialog.open(AddGlobalPermissionDialogComponent, {
      data: { globalPermissions: this.globalPermissions },
      width: '500px',
      panelClass: 'custom-dialog-container',
    });

    ref.afterClosed().subscribe((result) => {
      if (!result) return;

      const creates$ = result.permIds.map((permId: number) =>
        this.permissionService.createPermission(result.userId, permId),
      );

      forkJoin(creates$).subscribe({
        next: () => {
          this.permissionService.notifyUser(result.userId).subscribe();
          this.snackBar.open('Global permissions granted.', 'Close', {
            duration: 3000,
          });
          this.loadGlobalUsers();
        },
        error: () =>
          this.snackBar.open('Failed to grant permissions.', 'Close', {
            duration: 4000,
          }),
      });
    });
  }
  selectedTabIndex = 0;

  navigateToOrg(orgId: number): void {
    this.selectedTabIndex = 1; // Organizations tab index
    this.expandedOrgId = orgId;
    if (!this.orgRows[orgId]) {
      this.loadOrgUsers(orgId);
    }
  }
}
